import React,{useState,useMemo} from 'react';
import {motion} from 'framer-motion';
import AnalyticsChart from '../components/AnalyticsChart';
import PerformanceMetrics from '../components/PerformanceMetrics';
import SportBreakdown from '../components/SportBreakdown';
import TimeRangeSelector from '../components/TimeRangeSelector';
import ReturnPotential from '../components/ReturnPotential';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBarChart3,FiTrendingUp,FiPieChart,FiActivity,FiTarget}=FiIcons;

const Analytics=()=> {
  const [selectedRange,setSelectedRange]=useState('30d');
  const [activeTab,setActiveTab]=useState('overview');

  // Updated realistic data based on 8% average win rate and fee structure
  const mockData=useMemo(()=> {
    const generateData=(range)=> {
      // Base multipliers based on time range
      const rangeMultipliers={
        '7d': 0.15,
        '30d': 1.0,
        '90d': 3.2,
        '1y': 12.5,
        'all': 24.0
      };

      const multiplier=rangeMultipliers[range] || 1.0;

      // Realistic metrics based on 8% average win rate and fee structure
      const basePredictions=Math.round(125 * multiplier); // More predictions to show 8% win rate
      const baseWins=Math.round(basePredictions * 0.08); // 8% win rate
      const avgStakeAmount=850.00; // Average stake per prediction
      const avgFeeRate=0.075; // 7.5% average fee rate (between 5-10%)
      
      // Calculate realistic financial metrics
      const totalStaked=basePredictions * avgStakeAmount;
      const totalFeesPaid=totalStaked * avgFeeRate;
      const avgNetStake=avgStakeAmount * (1 - avgFeeRate); // Net after fees
      
      // Winners get 2-4x net staked back (using 3x average)
      const avgWinMultiplier=3.0;
      const totalWinnings=baseWins * avgNetStake * avgWinMultiplier;
      
      // Losers get net principal back
      const totalLosses=(basePredictions - baseWins);
      const principalReturned=totalLosses * avgNetStake;
      
      // Calculate ROI metrics
      const totalReturned=totalWinnings + principalReturned;
      const netProfit=totalReturned - totalStaked;
      const roiVsFees=(netProfit / totalFeesPaid) * 100; // ROI compared to fees paid
      const roiTokenAdjusted=(netProfit / totalStaked) * 100; // Overall ROI

      const baseMetrics={
        totalWins: baseWins,
        stakedPredictions: basePredictions,
        avgNetStake: avgNetStake,
        roiVsFees: roiVsFees,
        roiTokenAdjusted: roiTokenAdjusted,
        totalFeesPaid: totalFeesPaid,
        winRate: 8.0, // 8% win rate
        totalStaked: totalStaked,
        totalWinnings: totalWinnings,
        netProfit: netProfit,
        // Changes (for trend indicators)
        totalWinsChange: 12.5,
        stakedPredictionsChange: 18.3,
        avgNetStakeChange: -2.1,
        roiVsFeesChange: 28.7, // Substantial ROI improvement
        roiTokenAdjustedChange: 15.4,
        totalFeesPaidChange: 19.2
      };

      // Generate time-based labels and values
      const getTimeLabels=(range)=> {
        switch(range) {
          case '7d':
            return ['Day 1','Day 2','Day 3','Day 4','Day 5','Day 6','Day 7'];
          case '30d':
            return ['Week 1','Week 2','Week 3','Week 4'];
          case '90d':
            return ['Month 1','Month 2','Month 3'];
          case '1y':
            return ['Q1','Q2','Q3','Q4'];
          case 'all':
            return ['2022','2023','2024'];
          default:
            return ['Week 1','Week 2','Week 3','Week 4'];
        }
      };

      const generateValues=(baseValue,points,growth=1.15)=> {
        return Array.from({length: points},(_,i)=> 
          baseValue * Math.pow(growth,i) * (0.85 + Math.random() * 0.3)
        );
      };

      const labels=getTimeLabels(range);
      
      // Holdings data based on cumulative BBWIN returns
      const baseHoldings=Math.max(2400 * multiplier,1000);
      const baseUSD=Math.max(baseHoldings * 4.20,4200); // Assuming BBWIN = $4.20

      // Total BBWIN Holdings Over Time
      const holdingsOverTimeData={
        labels,
        values: generateValues(baseHoldings,labels.length,1.12)
      };

      // Total Holdings in USD
      const holdingsUSDData={
        labels: range==='7d' ? ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'] : labels,
        values: generateValues(baseUSD,labels.length,1.10)
      };

      const sportDistribution=[ 
        {name: 'Football',value: 42},
        {name: 'Basketball',value: 28},
        {name: 'Fighting',value: 18},
        {name: 'Tennis',value: 8},
        {name: 'Racing Sports',value: 4} 
      ];

      const performanceRadar={
        indicators: [ 
          {name: 'Win Rate',max: 100},
          {name: 'ROI Efficiency',max: 100},
          {name: 'Fee Management',max: 100},
          {name: 'Risk Control',max: 100},
          {name: 'Token Growth',max: 100} 
        ],
        values: [65,89,92,78,85] // Adjusted for 8% win rate but high ROI efficiency
      };

      // Realistic sport breakdown with 8% average win rate
      const sportBreakdown=[ 
        {
          name: 'Football',
          totalPredictions: Math.round(basePredictions * 0.42),
          totalStaked: totalStaked * 0.42,
          winRate: 8.5, // Slightly above average
          profit: netProfit * 0.45 // Higher share of profits
        },
        {
          name: 'Basketball',
          totalPredictions: Math.round(basePredictions * 0.28),
          totalStaked: totalStaked * 0.28,
          winRate: 7.8, // Slightly below average
          profit: netProfit * 0.25
        },
        {
          name: 'Fighting',
          totalPredictions: Math.round(basePredictions * 0.18),
          totalStaked: totalStaked * 0.18,
          winRate: 8.2,
          profit: netProfit * 0.20
        },
        {
          name: 'Tennis',
          totalPredictions: Math.round(basePredictions * 0.08),
          totalStaked: totalStaked * 0.08,
          winRate: 7.5, // Lower win rate
          profit: netProfit * 0.06
        },
        {
          name: 'Racing Sports',
          totalPredictions: Math.round(basePredictions * 0.04),
          totalStaked: totalStaked * 0.04,
          winRate: 9.2, // Higher win rate but small volume
          profit: netProfit * 0.04
        } 
      ];

      return {
        metrics: baseMetrics,
        holdingsOverTimeData,
        holdingsUSDData,
        sportDistribution,
        performanceRadar,
        sportBreakdown,
        multiplier
      };
    };

    return generateData(selectedRange);
  },[selectedRange]);

  const tabs=[ 
    {key: 'overview',label: 'Overview',icon: FiBarChart3},
    {key: 'performance',label: 'Performance',icon: FiTrendingUp},
    {key: 'sports',label: 'Sports Analysis',icon: FiPieChart},
    {key: 'trends',label: 'Trends',icon: FiActivity},
    {key: 'return-potential',label: 'Return Potential',icon: FiTarget}
  ];

  const renderTabContent=()=> {
    switch (activeTab) {
      case 'overview': 
        return ( 
          <div className="space-y-8"> 
            <PerformanceMetrics metrics={mockData.metrics} /> 
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> 
              <AnalyticsChart type="line" data={mockData.holdingsOverTimeData} title="Total BBWIN Holdings Over Time" height={350} /> 
              <AnalyticsChart type="bar" data={mockData.holdingsUSDData} title="Total Holdings in USD (Token Adjusted)" height={350} /> 
            </div> 
          </div> 
        );
      case 'performance': 
        return ( 
          <div className="space-y-8"> 
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> 
              <AnalyticsChart type="radar" data={mockData.performanceRadar} title="Performance Radar" height={400} /> 
              <div className="space-y-6"> 
                <div className="bg-bull-gray rounded-bull p-6 border border-bull-charcoal shadow-bull"> 
                  <h3 className="font-heading text-lg font-semibold text-bull-white mb-4"> 
                    Key Performance Insights 
                  </h3> 
                  <div className="space-y-4"> 
                    <div className="flex items-start gap-3"> 
                      <div className="w-2 h-2 bg-bull-yellow rounded-full mt-2"></div> 
                      <div> 
                        <p className="text-bull-white font-medium">Exceptional ROI Efficiency</p> 
                        <p className="text-bull-light-gray text-sm">{mockData.metrics.roiVsFees.toFixed(0)}% ROI vs fees - every $1 in fees generates ${(mockData.metrics.roiVsFees/100).toFixed(2)} in returns</p> 
                      </div> 
                    </div> 
                    <div className="flex items-start gap-3"> 
                      <div className="w-2 h-2 bg-bull-red rounded-full mt-2"></div> 
                      <div> 
                        <p className="text-bull-white font-medium">Sustainable Win Rate</p> 
                        <p className="text-bull-light-gray text-sm">{mockData.metrics.winRate}% win rate with 2-4x payout multiplier creates consistent growth</p> 
                      </div> 
                    </div> 
                    <div className="flex items-start gap-3"> 
                      <div className="w-2 h-2 bg-bull-yellow rounded-full mt-2"></div> 
                      <div> 
                        <p className="text-bull-white font-medium">Principal Protection</p> 
                        <p className="text-bull-light-gray text-small">Net principal returned on losses minimizes downside risk</p> 
                      </div> 
                    </div> 
                  </div> 
                </div> 
                <div className="bg-bull-gray rounded-bull p-6 border border-bull-charcoal shadow-bull"> 
                  <h3 className="font-heading text-lg font-semibold text-bull-white mb-4"> 
                    Financial Summary 
                  </h3> 
                  <div className="space-y-3"> 
                    <div className="flex justify-between items-center p-3 bg-bull-charcoal rounded-bull border border-bull-yellow/20"> 
                      <span className="text-bull-light-gray">Total Staked:</span>
                      <span className="text-bull-white font-bold">{mockData.metrics.totalStaked.toFixed(0)} BBWIN</span>
                    </div> 
                    <div className="flex justify-between items-center p-3 bg-bull-charcoal rounded-bull border border-bull-red/20"> 
                      <span className="text-bull-light-gray">Fees Paid:</span>
                      <span className="text-bull-white font-bold">{mockData.metrics.totalFeesPaid.toFixed(0)} BBWIN</span>
                    </div> 
                    <div className="flex justify-between items-center p-3 bg-bull-charcoal rounded-bull border border-bull-yellow/20"> 
                      <span className="text-bull-light-gray">Net Profit:</span>
                      <span className="text-bull-yellow font-bold">+{mockData.metrics.netProfit.toFixed(0)} BBWIN</span>
                    </div> 
                  </div> 
                </div> 
              </div> 
            </div> 
          </div> 
        );
      case 'sports': 
        return ( 
          <div className="space-y-8"> 
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> 
              <div className="bg-bull-gray rounded-bull p-6 border border-bull-charcoal shadow-bull"> 
                <h3 className="font-heading text-xl font-semibold text-bull-white mb-6"> 
                  Prediction Distribution by Sport 
                </h3> 
                <div className="h-[350px]"> 
                  <AnalyticsChart type="pie" data={mockData.sportDistribution} title="" height={350} /> 
                </div> 
              </div> 
              <SportBreakdown sportData={mockData.sportBreakdown} /> 
            </div> 
          </div> 
        );
      case 'trends': 
        return ( 
          <div className="space-y-8"> 
            <div className="grid grid-cols-1 gap-8"> 
              <AnalyticsChart type="line" data={{labels: mockData.holdingsOverTimeData.labels,values: mockData.holdingsOverTimeData.labels.map((_,i)=> 7.8 + (i * 0.3) + (Math.random() * 0.8))}} title="Win Rate Trend %" height={300} /> 
              <AnalyticsChart type="bar" data={{labels: ['Football','Basketball','Fighting','Tennis','Racing'],values: mockData.sportBreakdown.map(sport=> sport.totalPredictions * (sport.winRate/100))}} title="Actual Wins by Sport" height={300} /> 
            </div> 
          </div> 
        );
      case 'return-potential':
        return (
          <ReturnPotential 
            baseMetrics={mockData.metrics} 
            timeRange={selectedRange}
            multiplier={mockData.multiplier}
          />
        );
      default: 
        return null;
    }
  };

  return ( 
    <motion.div 
      initial={{opacity: 0,y: 20}} 
      animate={{opacity: 1,y: 0}} 
      transition={{duration: 0.5}} 
      className="space-y-8" 
    > 
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4"> 
        <div> 
          <h1 className="font-heading text-3xl font-bold text-bull-white mb-2"> 
            Analytics Dashboard 
          </h1> 
          <p className="text-bull-light-gray"> 
            Comprehensive analysis of your BBWIN prediction performance and ROI efficiency
          </p> 
        </div> 
        <TimeRangeSelector selectedRange={selectedRange} onRangeChange={setSelectedRange} /> 
      </div> 

      {/* Tab Navigation */} 
      <div className="flex bg-bull-charcoal rounded-bull p-1 overflow-x-auto border border-bull-gray"> 
        {tabs.map((tab)=> ( 
          <motion.button 
            key={tab.key} 
            className={`flex items-center gap-2 px-4 py-3 rounded-bull font-medium transition-all duration-300 whitespace-nowrap ${activeTab===tab.key ? 'bg-bull-red text-bull-white shadow-bull' : 'text-bull-light-gray hover:text-bull-white hover:bg-bull-gray'}`} 
            onClick={()=> setActiveTab(tab.key)} 
            whileHover={{scale: 1.02}} 
            whileTap={{scale: 0.98}} 
          > 
            <SafeIcon icon={tab.icon} className="w-4 h-4" /> 
            <span>{tab.label}</span> 
          </motion.button> 
        ))} 
      </div> 

      {/* Tab Content */} 
      <motion.div 
        key={activeTab} 
        initial={{opacity: 0,y: 20}} 
        animate={{opacity: 1,y: 0}} 
        transition={{duration: 0.3}} 
      > 
        {renderTabContent()} 
      </motion.div> 
    </motion.div> 
  );
};

export default Analytics;